'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "sx", "classes"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageListDateDivider } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageUtilityClasses } from "./chatMessageClasses.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatDateDivider');
const ChatDateDividerStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'DateDivider',
  overridesResolver: (_, styles) => styles.dateDivider
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(1),
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.disabled,
  userSelect: 'none'
}));
const ChatDateDividerLineStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'DateDividerLine'
})(({
  theme
}) => ({
  flex: 1,
  height: 1,
  backgroundColor: (theme.vars || theme).palette.divider
}));
const ChatDateDividerLabelStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'DateDividerLabel'
})(({
  theme
}) => ({
  fontSize: theme.typography.caption.fontSize,
  color: (theme.vars || theme).palette.text.disabled,
  whiteSpace: 'nowrap'
}));
const ChatDateDivider = /*#__PURE__*/React.forwardRef(function ChatDateDivider(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatDateDivider'
  });
  // Drop a JS/theme-injected `classes` (not a prop on this sub-part — it shares
  // the `MuiChatMessage-*` namespace) so it can't leak onto the DOM via `...other`.
  const _ref = props,
    {
      slots,
      slotProps,
      className,
      sx,
      classes: classesProp
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  void classesProp;
  const classes = useChatMessageUtilityClasses(undefined);
  return /*#__PURE__*/_jsx(MessageListDateDivider, _extends({
    ref: ref
  }, other, {
    slots: _extends({}, slots, {
      divider: slots?.divider ?? ChatDateDividerStyled,
      line: slots?.line ?? ChatDateDividerLineStyled,
      label: slots?.label ?? ChatDateDividerLabelStyled
    }),
    slotProps: _extends({}, slotProps, {
      divider: mergeSlotProps({
        className: clsx(classes.dateDivider, className),
        sx
      }, slotProps?.divider),
      line: mergeSlotProps({
        className: classes.dateDividerLine
      }, slotProps?.line),
      label: mergeSlotProps({
        className: classes.dateDividerLabel
      }, slotProps?.label)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatDateDivider.displayName = "ChatDateDivider";
process.env.NODE_ENV !== "production" ? ChatDateDivider.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  className: PropTypes.string,
  formatDate: PropTypes.func,
  index: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.string),
  messageId: PropTypes.string.isRequired,
  /**
   * Decides whether the divider renders above the message, replacing the
   * built-in rule when provided. `date`/`previousDate` are the parsed
   * `createdAt` values (`null` when missing or invalid); `previousMessage`
   * is `null` for the first message in the list.
   * @param {object} params The parameters used to decide whether to render the divider.
   * @param {ChatMessage} params.message The message the divider would render above.
   * @param {ChatMessage | null} params.previousMessage The previous message, or `null` for the first message in the list.
   * @param {number} params.index The index of the message in the list.
   * @param {Date | null} params.date The parsed `createdAt` of the message, or `null` when missing or invalid.
   * @param {Date | null} params.previousDate The parsed `createdAt` of the previous message, or `null` when missing or invalid.
   * @returns {boolean} `true` to render the divider above the message.
   * @default Renders when `message.createdAt` falls on a different UTC
   * calendar day than the previous message's.
   */
  shouldShowDivider: PropTypes.func,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatDateDivider };